import { useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, UploadSimple } from "@phosphor-icons/react";

// Column-name aliases accepted by the importer (so operators can paste rows
// directly from their existing dealer spreadsheets without remapping):
//   - chasis_number / chassis_number → vin
//   - enguine_number / engine_number → engine_no
//   - date_of_purchase / purchase_date → purchased_on
//   - dealer_code / dlr_code / tvs_dealer_code → dealer_code (numeric)
// `type` is OPTIONAL — when missing, inferred from the model/make text
// (SCOOTERS / MOTORCYCLE → bike, CAR/SEDAN → car, TRUCK/LORRY → truck).
// `packages` is pipe-separated (e.g., "Pkg A|Pkg B").
const TEMPLATE_HEADER =
  "chasis_number,enguine_number,date_of_purchase,dealer_code,make,model,year,price,color,mileage,status,image_url,notes,packages\n" +
  "MD626AK44T1F21391,GK4FT1502461,2026-06-10,12345,TVS,SCOOTERS,2026,69575.44,P WHITE,50,available,,,JUPITER 125 BSVI-DISC\n";

export default function VehicleCsvIO({ onImported, canImport, filters }) {
  const inputRef = useRef(null);

  const download = async () => {
    try {
      const res = await api.get("/vehicles/export", {
        responseType: "blob",
        params: filters || {},
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vehicles-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_HEADER], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vehicles-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/vehicles/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parts = [`Imported ${data.imported}`];
      if (data.updated != null) parts.push(`updated ${data.updated}`);
      parts.push(`skipped ${data.skipped}`);
      const msg = parts.join(", ");
      if (data.errors?.length) {
        toast.warning(`${msg}. First issue: ${data.errors[0]}`, { duration: 8000 });
      } else {
        toast.success(msg);
      }
      onImported?.();
    } catch (err) {
      // Surface strict referential-integrity failures with the missing
      // package list so the admin knows what to seed before re-uploading.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing_packages?.length) {
        const sample = detail.missing_packages.slice(0, 5).join(", ");
        const more = detail.missing_packages.length > 5 ? ` (+${detail.missing_packages.length - 5} more)` : "";
        toast.error(`${detail.message} Missing: ${sample}${more}`, { duration: 10000 });
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        onChange={upload}
        className="hidden"
        data-testid="vehicle-csv-input"
      />
      <button
        type="button"
        onClick={downloadTemplate}
        data-testid="vehicle-csv-template"
        className="px-3 py-2 border border-canvas-line text-sm hover:border-ink flex items-center gap-1.5"
        title="Download a blank CSV with the correct headers"
      >
        <DownloadSimple size={13} weight="bold" />
        Template
      </button>
      {canImport && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid="vehicle-csv-import"
          className="px-3 py-2 border border-canvas-line text-sm hover:border-ink flex items-center gap-1.5"
        >
          <UploadSimple size={13} weight="bold" />
          Import CSV
        </button>
      )}
      <button
        type="button"
        onClick={download}
        data-testid="vehicle-csv-export"
        className="px-3 py-2 border border-canvas-line text-sm hover:border-ink flex items-center gap-1.5"
      >
        <DownloadSimple size={13} weight="bold" />
        Export CSV
      </button>
    </div>
  );
}
